"use client";

import { FEATURED_SPECIALTY_PILLS_MAX, SPECIALTIES } from "@klarify/constants";
import { Link } from "@klarify/i18n/navigation";
import type {
	FeaturedDirectoryEntry,
	FeaturedDirectoryPost,
} from "@klarify/types";
import { classifyTrafficSource, labelForId } from "@klarify/utilities";
import { Trans, useLingui } from "@lingui/react/macro";
import { FileText, Mail, MapPin } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useId } from "react";
import { cn } from "../../lib/utils";
import { api } from "../../trpc/client";
import {
	alreadyTrackedRecently,
	getOrCreateVisitorId,
} from "../clinic-stats/visitor-id";
import { ContactCta } from "./contact-cta";
import { DirectoryHeadshot } from "./directory-headshot";
import { formatLocation } from "./format-location";

type ClickTarget = "contact" | "profile" | "post";

type Props = {
	entry: FeaturedDirectoryEntry;
	/** 0-based position in the featured row; reported with every event. */
	slot: number;
};

export const FeaturedTherapistCard = ({ entry, slot }: Props) => {
	const { t } = useLingui();
	const headingId = useId();
	// `mutate` is referentially stable across renders (react-query wraps it in
	// useCallback), so effects keyed on it do not re-fire on state changes.
	const trackEvent =
		api.trackingRouter.trackPublicPageview.useMutation().mutate;
	const { clinicId, clinicSlug, therapistId, placement } = entry;

	const send = useCallback(
		(
			event: "featured_therapist_impression" | "featured_therapist_clicked",
			target?: ClickTarget,
		) => {
			trackEvent({
				distinctId: getOrCreateVisitorId(),
				event,
				properties: {
					clinicId,
					clinicSlug,
					therapistId,
					slot,
					placement,
					target,
					source: classifyTrafficSource(
						document.referrer || undefined,
						window.location.hostname,
					),
					locale: document.documentElement.lang || undefined,
				},
			});
		},
		[trackEvent, clinicId, clinicSlug, therapistId, slot, placement],
	);

	useEffect(() => {
		// 30-min dedup so back-nav and remounts don't inflate sponsor impressions.
		if (alreadyTrackedRecently(`featured:${clinicId}`)) {
			return;
		}
		send("featured_therapist_impression");
	}, [clinicId, send]);

	const fullName = [entry.firstName, entry.lastName].filter(Boolean).join(" ");
	const heading = entry.credentialsSuffix
		? `${fullName}, ${entry.credentialsSuffix}`
		: fullName;
	const location = formatLocation({
		country: entry.operatingCountry,
		state: entry.operatingState,
		city: entry.operatingCity,
	});
	const bio = entry.bioExcerpt ?? entry.tagline;
	const specialties = entry.specialties.slice(0, FEATURED_SPECIALTY_PILLS_MAX);

	return (
		<li className="@container flex flex-col overflow-hidden rounded-2xl border border-klarify-forest-200 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
			{/* The row goes two-up at md while cards can still be ~350px wide, so
			    the post column keys on the card's own width, not the viewport. */}
			<div
				className={cn(
					"grid gap-6 p-6",
					entry.latestPost ? "@[34rem]:grid-cols-[minmax(0,1fr)_240px]" : null,
				)}
			>
				<div className="flex min-w-0 flex-col gap-3">
					<div className="flex items-start gap-3.5">
						<DirectoryHeadshot
							src={entry.profilePicture}
							firstName={entry.firstName}
							lastName={entry.lastName}
							size={72}
							className="size-[72px] shrink-0 rounded-[14px] object-cover"
							fallbackClassName="text-2xl"
						/>
						<div className="flex min-w-0 flex-col gap-1">
							<h3
								id={headingId}
								className="font-semibold text-klarify-neutral-900 text-lg leading-[1.3] tracking-[-0.02em]"
							>
								{heading}
							</h3>
							{entry.pronouns ? (
								<p className="text-klarify-neutral-500 text-xs">
									{entry.pronouns}
								</p>
							) : null}
							{location ? (
								<p className="inline-flex min-w-0 items-center gap-1.5 text-[13px] text-klarify-neutral-600">
									<MapPin className="size-[13px] shrink-0 text-klarify-neutral-400" />
									<span className="truncate">{location}</span>
								</p>
							) : null}
						</div>
					</div>
					{bio ? (
						<p className="line-clamp-3 text-klarify-neutral-700 text-sm leading-[1.6]">
							{bio}
						</p>
					) : null}
					{specialties.length > 0 ? (
						<div className="flex flex-wrap gap-1.5">
							{specialties.map((id) => (
								<span
									key={id}
									className="rounded-sm border border-klarify-neutral-200 bg-klarify-neutral-50 px-2 py-[3px] text-klarify-neutral-700 text-xs leading-snug"
								>
									{labelForId(SPECIALTIES, id)}
								</span>
							))}
						</div>
					) : null}
				</div>
				{entry.latestPost ? (
					<FeaturedLatestPost
						post={entry.latestPost}
						firstName={entry.firstName}
						onClick={() => send("featured_therapist_clicked", "post")}
					/>
				) : null}
			</div>
			<div className="mt-auto flex flex-wrap gap-2 border-klarify-neutral-100 border-t bg-klarify-cloud-50/40 px-6 py-3">
				<ContactCta
					slug={entry.clinicSlug}
					therapistId={entry.therapistId ?? undefined}
					firstName={entry.firstName}
					lastName={entry.lastName}
					credentialsSuffix={entry.credentialsSuffix}
					label={t`Contact ${{ name: entry.firstName }}`}
					leadingIcon={<Mail className="size-3.5" />}
					onOpen={() => send("featured_therapist_clicked", "contact")}
					className="h-9 gap-1.5 rounded-md px-4 font-medium text-[13px] shadow-none hover:shadow-none"
				/>
				<Link
					href={entry.href}
					aria-describedby={headingId}
					onClick={() => send("featured_therapist_clicked", "profile")}
					className="inline-flex h-9 items-center rounded-md border border-klarify-neutral-200 bg-white px-4 font-medium text-[13px] text-klarify-neutral-700 transition-colors hover:border-klarify-forest-200 hover:text-klarify-forest-800"
				>
					<Trans>View profile</Trans>
				</Link>
			</div>
		</li>
	);
};

FeaturedTherapistCard.displayName = "FeaturedTherapistCard";

const FeaturedLatestPost = ({
	post,
	firstName,
	onClick,
}: {
	post: FeaturedDirectoryPost;
	firstName: string;
	onClick: () => void;
}) => {
	const { i18n } = useLingui();
	const hasMinutes = post.minutesToRead > 0;
	return (
		<Link
			href={post.href}
			onClick={onClick}
			className="group flex flex-col overflow-hidden rounded-lg border border-klarify-neutral-100 bg-klarify-cloud-50/40 transition-colors hover:border-klarify-forest-200"
		>
			{post.coverImageUrl ? (
				// The tile is a fixed ~240px column, so a 2x intrinsic size and no
				// `sizes` keeps the srcset to 1x/2x instead of the deviceSizes ladder.
				// The row sits above the fold, so the cover is the page's LCP candidate.
				<div className="aspect-video w-full overflow-hidden">
					<Image
						src={post.coverImageUrl}
						alt=""
						width={480}
						height={270}
						priority
						className="h-full w-full object-cover"
					/>
				</div>
			) : (
				<div className="flex aspect-video items-center justify-center bg-klarify-peach-50 text-klarify-peach-700">
					<FileText className="size-[22px]" />
				</div>
			)}
			<div className="flex flex-1 flex-col gap-1.5 px-3.5 pt-3 pb-3.5">
				<p className="text-[10px] text-klarify-neutral-400 uppercase tracking-[0.08em]">
					<Trans>Latest from {{ name: firstName }}</Trans>
				</p>
				<p className="line-clamp-3 font-medium text-[13px] text-klarify-neutral-900 leading-[1.4]">
					{post.title}
				</p>
				{hasMinutes || post.publishedAt ? (
					<p className="mt-auto flex items-center gap-1.5 text-[10px] text-klarify-neutral-400">
						{hasMinutes ? (
							<span>
								<Trans>{{ minutes: post.minutesToRead }} min read</Trans>
							</span>
						) : null}
						{hasMinutes && post.publishedAt ? (
							<span className="text-klarify-cloud-400">·</span>
						) : null}
						{post.publishedAt ? (
							<time dateTime={post.publishedAt.toISOString()}>
								{new Intl.DateTimeFormat(i18n.locale, {
									month: "short",
									day: "numeric",
									timeZone: "UTC",
								}).format(post.publishedAt)}
							</time>
						) : null}
					</p>
				) : null}
			</div>
		</Link>
	);
};
FeaturedLatestPost.displayName = "FeaturedLatestPost";
